/**
 * squeeze-compat — making the docked sidebar work on hosts that do NOTHING
 * for us (FLO-523). The Qualified-parity harness, behind per-site flags.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * The v1 squeeze ({@link ./host-squeeze}) gives `<html>` a `margin-right` and
 * publishes `--floe-docked-viewport-offset` so the host can offset its own
 * `position: fixed` chrome. That is COOPERATION: a fixed navbar's containing
 * block is the viewport, not `<html>`, so it keeps its full width and sits under
 * the rail unless the customer edits their CSS. floe.so edits its CSS. An
 * arbitrary customer site will not.
 *
 * This module is the zero-cooperation path, verified live on qualified.com
 * (2026-08-11 forensics in `.context/FLO-514-sidebar-implementation-plan.md`).
 *
 * ── The two flags (both default FALSE; absent config = off) ────────────────
 *
 * `containment` — one INSEPARABLE bundle, not four switches:
 *   1. `html { contain: layout }` makes `<html>` the containing block for EVERY
 *      `position: fixed` element on the page. An uncooperative `width:100%` /
 *      `left:0;right:0` navbar now squeezes with the page, for free.
 *      MEASURED, not assumed (Chrome 151.0.7922.138, 1440×900, 400px dock, real
 *      modules bundled from client/src): `container-type: inline-size` alone
 *      does NOT contain fixed descendants — a fixed navbar and a
 *      `left:0;right:0` banner both stayed 1440px wide while `<html>`'s own box
 *      was 1040 — and neither does `container-type: size`, on the root or on a
 *      plain div. `contain: layout` squeezes both to 1040 on the same page.
 *      (Chrome also reports `getComputedStyle(html).contain === 'none'` for a
 *      container-type element, so the qualified.com forensics line in the
 *      FLO-514 plan — "`container-type: inline-size` → `contain: inline-size
 *      layout`" — was almost certainly reading an explicit `contain`
 *      declaration of theirs.) We ship BOTH: `contain: layout` is the
 *      load-bearing declaration; `container-type` stays for parity with the
 *      reference harness and for any engine that does fold layout containment
 *      into it, where it is simply redundant.
 *   2. …which forces `html { overflow: hidden }` + body-as-scroller: once fixed
 *      elements are contained they behave like `absolute` inside `<html>`, so if
 *      `<html>` were still the scroller they would scroll away with the content.
 *      Body scrolling keeps them visually fixed. (Bonus: the scrollbar lands at
 *      the squeezed edge instead of under the rail.)
 *   3. …which breaks `window.scrollY` / `scrollTo` (they read the viewport
 *      scroller, which now never moves), so the scroll facade is mandatory, not
 *      optional.
 *   4. …and it re-contains OUR OWN fixed containers too, so the SDK root escapes
 *      via the top layer (Popover API). No `showPopover` → the whole bundle is
 *      refused (see {@link resolveCompatPlan}); shipping 1–3 without 4 would
 *      trap the rail inside the squeezed box.
 *   Applied ONCE at boot and never toggled per open/close — flipping containment
 *   mid-session relays out every fixed element on the page (Qualified applies it
 *   statically for the same reason).
 *
 * `viewportFacade` — patched `window.innerWidth` + `window.matchMedia` reporting
 *   viewport-minus-dock while docked, plus a synthetic `resize` on dock/undock,
 *   so host RESPONSIVE JS follows the squeeze. Independent of containment.
 *   CSS `@media` cannot be patched — host stylesheets still see the real
 *   viewport. That is unfixable in general (Qualified has the same hole) and is
 *   why the rail only squeezes at ≥1024px.
 *
 * ── Support matrix ─────────────────────────────────────────────────────────
 *   | engine                         | containment | facade | result            |
 *   | Chrome 114+/Edge, Safari 17+,  |     yes     |  yes   | full harness      |
 *   |   Firefox 125+ (popover)       |             |        |                   |
 *   | Safari 16.4–16.6, older Chrome |     NO      |  yes   | v1 squeeze + facade|
 *   |   (no Popover API)             |             |        | (fixed host chrome |
 *   |                                |             |        |  needs the var)   |
 *   | boot viewport < 1024px         |     NO      |  yes   | rail never squeezes|
 *   |                                |             |        | at that tier anyway|
 * A refused bundle is a DEGRADE, never a failure: the site keeps exactly the v1
 * behaviour it would have had with the flag off.
 *
 * ── What is deliberately NOT covered (be honest, don't paper over) ─────────
 *   - `document.documentElement.scrollTop` READS are proxied (own accessor), but
 *     any code that captured a reference to the descriptor before boot is not.
 *   - CSS `@media` in host stylesheets (see above).
 *   - **A `MediaQueryList` the host registered BEFORE we booted — which is the
 *     normal idiom, since the SDK loads after page load — never follows the
 *     squeeze and never fires `change` on dock/undock.** MEASURED: a
 *     `(min-width: 1100px)` MQL created by the host page stayed `matches: true`
 *     across a full dock AND undock at a reported 1040px, 0 `change` events,
 *     while a query created in the same docked frame correctly returned false.
 *     A live MQL's query cannot be rewritten from outside, so the synthetic
 *     `resize` is the ONLY signal such a host gets; resize-handler code follows
 *     the squeeze, MQL-listener code does not. (The reverse direction is also
 *     true and rarer: a MQL obtained WHILE docked keeps its shifted query after
 *     undock.)
 *   - Scroll EXTENT beyond `documentElement.scrollHeight`/`scrollWidth` (which
 *     ARE proxied to the body scroller): with the body scroller installed,
 *     `documentElement.offsetHeight`, `body.offsetHeight` and `body.clientHeight`
 *     all report one viewport instead of the content height (measured 813 vs
 *     3091). `body.scrollHeight` and the `Math.max(...)` idiom jQuery's
 *     `$(document).height()` uses stay honest, so this bites only the
 *     `innerHeight + scrollY >= body.offsetHeight` flavour of infinite scroll.
 *   - The SDK's own guidance overlay (light DOM, `ui-rendering.ts`) is NOT
 *     top-layer-escaped. It is inert in website-agent mode (no host driving),
 *     which is the only mode this harness runs in.
 */

import { DOCKED_ATTR, normalizeSqueezeWidth } from './host-squeeze';
import { clearNativeViewport, setNativeViewport } from './native-viewport';
import { matchesViewportTier } from '../hooks/useViewportTier';

/** id of the single injected harness `<style>`. Cross-package contract. */
export const COMPAT_STYLE_ID = 'floe-host-squeeze-compat';

/**
 * Marks the SDK root that we lifted into the top layer, so the harness CSS can
 * neutralise the UA's `[popover]` styling without knowing the element's id.
 */
export const TOP_LAYER_ATTR = 'data-floe-top-layer';

// ---------------------------------------------------------------------------
// Config facet
// ---------------------------------------------------------------------------

/** The resolved `websiteAgent.sidebarCompat` facet. */
export interface SidebarCompat {
  /** The full containment bundle (see the module docblock). */
  containment: boolean;
  /** Patched innerWidth/matchMedia + synthetic resize. */
  viewportFacade: boolean;
}

/** Both off — the shape every absent/garbage config resolves to. */
export const SIDEBAR_COMPAT_OFF: SidebarCompat = Object.freeze({
  containment: false,
  viewportFacade: false,
});

/**
 * Server/host config → flags we're willing to act on.
 *
 * Only a literal `true` turns anything on: a stale blob, a string `"true"`, a
 * truthy object or an older floe-api build that never heard of `sidebarCompat`
 * all resolve to OFF. These flags rewrite the customer's document scroller and
 * patch their globals — "I could not parse this" must never mean "yes".
 */
export function resolveSidebarCompat(raw: unknown): SidebarCompat {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...SIDEBAR_COMPAT_OFF };
  const rec = raw as Record<string, unknown>;
  return {
    containment: rec.containment === true,
    viewportFacade: rec.viewportFacade === true,
  };
}

/** True when at least one flag asks for something. */
export function isCompatRequested(compat: SidebarCompat): boolean {
  return compat.containment || compat.viewportFacade;
}

// ---------------------------------------------------------------------------
// Capability negotiation (pure)
// ---------------------------------------------------------------------------

export interface CompatCapabilities {
  /** Popover API — the top layer is the ONLY way out of our own containment. */
  popover: boolean;
  /** The SDK root exists and can be lifted. */
  root: boolean;
  /** Boot viewport is in the squeeze tier (≥1024px). */
  squeezeTier: boolean;
}

/**
 * Which parts of the requested harness we can actually honour.
 *
 * containment is all-or-nothing and gated on THREE things:
 *   - `showPopover` — without the top layer our own rail gets contained into the
 *     squeezed box (it would render over the page instead of beside it).
 *   - a root element to lift.
 *   - the BOOT viewport tier. The harness is static for the session (toggling it
 *     later is the layout earthquake we're avoiding), so a phone/tablet visitor
 *     — who never squeezes anything — must not have their document scroller
 *     rewritten for the whole visit. A visitor who later widens past 1024 simply
 *     gets the v1 squeeze; nothing breaks, uncooperative fixed chrome just stays
 *     full-width.
 *
 * The facade has no such dependency: it is pure JS and correct at every width
 * (it reports the real viewport whenever the page is not docked).
 */
export function resolveCompatPlan(
  requested: SidebarCompat,
  caps: CompatCapabilities,
): SidebarCompat {
  return {
    containment: requested.containment && caps.popover && caps.root && caps.squeezeTier,
    viewportFacade: requested.viewportFacade,
  };
}

/** Runtime feature detection for {@link resolveCompatPlan}. */
export function detectCompatCapabilities(root: unknown): CompatCapabilities {
  const popover =
    typeof HTMLElement !== 'undefined' &&
    typeof (HTMLElement.prototype as { showPopover?: unknown }).showPopover === 'function';
  return {
    popover,
    root: !!root,
    // matchesViewportTier() reads media queries through native-viewport, so it
    // is honest even if a previous facade were somehow still installed.
    squeezeTier: matchesViewportTier() === 'squeeze',
  };
}

// ---------------------------------------------------------------------------
// The harness stylesheet (pure)
// ---------------------------------------------------------------------------

/**
 * The containment harness CSS.
 *
 * @param bodyInsetPx the host body's total vertical margin, measured at boot.
 *        `body { height: 100% }` measures against `<html>`'s content box, so on
 *        a page that kept the UA's default 8px body margin the scroller would be
 *        16px taller than the box that clips it and the bottom 16px would be
 *        unreachable. Subtracting the measured margin makes the scroller exactly
 *        the visible strip. 0 on the (overwhelmingly common) reset-to-zero site.
 *
 * `!important` where — and only where — the harness is load-bearing:
 *   - `html{overflow:hidden}`: a host `html{overflow-y:scroll}` (the classic
 *     scrollbar-stability trick) or a CSS-in-JS rule injected AFTER our sheet
 *     would otherwise win and let the contained "fixed" chrome scroll away.
 *   - `body{height}`/`min-height`: the scroller must be exactly one viewport. A
 *     host `body{min-height:100vh}` would grow it past the clipping box and the
 *     page would become partly unreachable.
 * NOT on `body{overflow-y:auto}` on purpose: a host that locks its own page for
 * a modal does it with `body{overflow:hidden}`, and `!important` here would
 * defeat that lock — a worse bug than the rare site that ships a static
 * `body{overflow:hidden}`.
 */
export function buildCompatCss(bodyInsetPx: unknown = 0): string {
  const inset = normalizeSqueezeWidth(bodyInsetPx);
  const bodyHeight = inset > 0 ? `calc(100% - ${inset}px)` : '100%';
  return [
    // 1+2. Containment, and the body-scroller it forces.
    // `contain:layout` is the declaration that actually re-parents the host's
    // `position:fixed` chrome into <html>'s box (measured in Chrome 151 —
    // `container-type` alone leaves a fixed navbar full-width; see the module
    // docblock). `container-type:inline-size` rides along for parity with the
    // reference harness and for engines that fold layout containment into it.
    `html{container-type:inline-size;contain:layout;overflow:hidden !important;height:100% !important;}`,
    `body{height:${bodyHeight} !important;min-height:0 !important;overflow-y:auto;}`,
    // The 100vw/w-screen clip moves WITH the scroller: v1 puts `overflow-x` on
    // <html>, which no longer scrolls, so while docked the body has to carry it
    // or overflowing host content adds a horizontal scrollbar to the new
    // scroller. `clip` last (it can never create a scroll container, so host
    // `position:sticky` survives); `hidden` first for pre-clip engines.
    `html[${DOCKED_ATTR}] body{overflow-x:hidden;overflow-x:clip;}`,
    // 4. Top-layer escape. The UA sheet dresses an open popover as a dialog —
    // `background-color:canvas` alone would paint an OPAQUE full-viewport panel
    // over the customer's site — so every UA declaration is neutralised here.
    // Author origin beats UA origin, so no `!important` is needed.
    `[${TOP_LAYER_ATTR}]{position:fixed;inset:0;width:100%;height:100%;margin:0;border:0;` +
      `padding:0;background:transparent;color:inherit;overflow:visible;max-width:none;` +
      `max-height:none;pointer-events:none;}`,
    `[${TOP_LAYER_ATTR}]::backdrop{background:transparent;pointer-events:none;}`,
  ].join('\n');
}

// ---------------------------------------------------------------------------
// Media-query rewriting (pure)
// ---------------------------------------------------------------------------

/**
 * `(min-width: Npx)` / `(max-width: Npx)` in a query, in the simple form. The
 * range syntax (`(width >= 600px)`) and non-px units are deliberately NOT
 * matched — passing an untouched query through is always safe (the host sees the
 * real viewport, i.e. v1 behaviour), whereas a half-understood rewrite is not.
 */
const WIDTH_FEATURE_RE = /\(\s*(min-width|max-width)\s*:\s*(\d+(?:\.\d+)?)px\s*\)/gi;

/**
 * Re-express a media query so that the NATIVE engine, evaluating against the
 * REAL viewport, answers the question the host actually asked about the REDUCED
 * one.
 *
 * With `reported = real − offset`:
 *   `(min-width: N)` matches ⟺ reported ≥ N ⟺ real ≥ N + offset
 *   `(max-width: N)` matches ⟺ reported ≤ N ⟺ real ≤ N + offset
 * so both shift UP by the dock width.
 *
 * Shifting the query rather than hand-rolling a fake MediaQueryList is what
 * keeps the result LIVE: the host gets a genuine MQL that fires `change` on
 * resize, with working `addEventListener`/`matches`/`media`. The cost is that a
 * query obtained while docked stays shifted after undock — the synthetic
 * `resize` is the signal to re-query.
 */
export function rewriteMediaQuery(query: string, offsetPx: number): string {
  const offset = normalizeSqueezeWidth(offsetPx);
  if (offset <= 0 || typeof query !== 'string' || !query) return query;
  return query.replace(WIDTH_FEATURE_RE, (whole, feature: string, value: string) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return whole;
    return `(${feature.toLowerCase()}: ${n + offset}px)`;
  });
}

// ---------------------------------------------------------------------------
// Property patching (pure-ish helpers over a target object)
// ---------------------------------------------------------------------------

/**
 * A displaced property, with everything needed to put it back EXACTLY as found:
 * either its own descriptor, or the fact that it had none (in which case the
 * restore is a `delete`, which un-shadows the prototype's accessor).
 */
interface PatchedProperty {
  target: object;
  prop: string;
  /** The own descriptor we displaced, if there was one. */
  previous?: PropertyDescriptor;
}

/** Walk the prototype chain — `window.innerWidth` may be own OR inherited. */
function findDescriptor(target: object, prop: string): PropertyDescriptor | undefined {
  let cursor: object | null = target;
  while (cursor) {
    const desc = Object.getOwnPropertyDescriptor(cursor, prop);
    if (desc) return desc;
    cursor = Object.getPrototypeOf(cursor);
  }
  return undefined;
}

/** Read a property through a saved descriptor (accessor OR data — test stubs). */
function readThrough(desc: PropertyDescriptor | undefined, receiver: object): unknown {
  if (!desc) return undefined;
  return desc.get ? desc.get.call(receiver) : desc.value;
}

/**
 * Install `replacement` over `target[prop]`, returning the receipt to restore.
 * Null when the property refuses to budge (non-configurable, frozen global) —
 * the caller then simply does not patch that one, rather than throwing halfway
 * through the harness and leaving the page half-faked.
 */
function patchProperty(
  target: object,
  prop: string,
  replacement: PropertyDescriptor,
): PatchedProperty | null {
  const previous = Object.getOwnPropertyDescriptor(target, prop);
  if (previous && previous.configurable === false) return null;
  try {
    Object.defineProperty(target, prop, {
      configurable: true,
      enumerable: previous?.enumerable ?? true,
      ...replacement,
    });
  } catch {
    return null;
  }
  return { target, prop, previous };
}

/** Put one displaced property back byte-for-byte. */
function restoreProperty(patch: PatchedProperty): void {
  try {
    if (patch.previous) {
      Object.defineProperty(patch.target, patch.prop, patch.previous);
    } else {
      delete (patch.target as Record<string, unknown>)[patch.prop];
    }
  } catch {
    /* nothing sensible left to do; never throw out of teardown */
  }
}

// ---------------------------------------------------------------------------
// DOM driver
// ---------------------------------------------------------------------------

export interface ApplyCompatOptions {
  /** The resolved (server) flags. */
  compat: SidebarCompat;
  /** The SDK's fixed root — `#onboarding-sdk-react-ui`. */
  root?: HTMLElement | null;
  /** Configured rail width in px, or a getter if it can change. */
  dockWidth: number | (() => number);
  /** Test seam — real capabilities are detected when omitted. */
  capabilities?: CompatCapabilities;
}

/** Everything one active harness has to undo, newest first. */
interface ActiveHarness {
  plan: SidebarCompat;
  teardowns: Array<() => void>;
}

let active: ActiveHarness | null = null;

function hasDom(): boolean {
  return (
    typeof document !== 'undefined' &&
    !!document.documentElement &&
    !!document.head &&
    typeof document.createElement === 'function'
  );
}

/** Live "is the rail docked right now?" — the v1 attribute is the only truth. */
function isDocked(): boolean {
  try {
    const html = document.documentElement as unknown as {
      hasAttribute?: (name: string) => boolean;
    };
    return typeof html?.hasAttribute === 'function' ? html.hasAttribute(DOCKED_ATTR) : false;
  } catch {
    return false;
  }
}

/**
 * Total vertical margin on `<body>`, for the scroller height. Best effort: any
 * failure (no getComputedStyle in the node env) yields 0, which is exactly right
 * for every site that resets body margins.
 */
function measureBodyInset(): number {
  try {
    const body = document.body;
    if (!body || typeof getComputedStyle !== 'function') return 0;
    const cs = getComputedStyle(body);
    const top = parseFloat(cs.marginTop || '0');
    const bottom = parseFloat(cs.marginBottom || '0');
    const total = (Number.isFinite(top) ? top : 0) + (Number.isFinite(bottom) ? bottom : 0);
    return total > 0 ? Math.round(total) : 0;
  } catch {
    return 0;
  }
}

/** Best-effort current viewport scroll offset, read through native APIs only. */
function readViewportScrollTop(): number {
  try {
    const y = (window as unknown as { scrollY?: unknown }).scrollY;
    if (typeof y === 'number' && Number.isFinite(y) && y > 0) return y;
    const html = document.documentElement as unknown as { scrollTop?: unknown };
    return typeof html?.scrollTop === 'number' && Number.isFinite(html.scrollTop)
      ? html.scrollTop
      : 0;
  } catch {
    return 0;
  }
}

function writeScrollTop(el: unknown, y: number): void {
  try {
    if (el && y > 0) (el as { scrollTop: number }).scrollTop = y;
  } catch {
    /* a stubborn host element; a lost scroll position is not worth throwing */
  }
}

/**
 * Inject the harness sheet; returns its removal.
 *
 * Both directions CARRY THE SCROLL POSITION. Handing the scroll job from
 * `<html>` to `<body>` starts the new scroller at 0, so a visitor who had
 * already scrolled (the SDK boots after an async validate-client-key round trip
 * — they usually have) gets yanked back to the top of the page the moment the
 * widget loads. Same jump in reverse on teardown.
 */
function installHarnessCss(): () => void {
  const doc = document;
  const carried = readViewportScrollTop();
  const existing = doc.getElementById(COMPAT_STYLE_ID) as HTMLStyleElement | null;
  const el = existing || doc.createElement('style');
  el.id = COMPAT_STYLE_ID;
  // Print output keeps the host's own scroller/containment — the rail isn't
  // printed either (v1 does the same).
  el.setAttribute('media', 'screen');
  el.textContent = buildCompatCss(measureBodyInset());
  if (!existing) doc.head.appendChild(el);
  writeScrollTop(doc.body, carried);

  return () => {
    // Read the body scroller BEFORE the sheet goes (afterwards it is not the
    // scroller any more and reports 0). The scroll facade has already been
    // restored by the time this runs — teardown unwinds in reverse — so both
    // sides of this are native.
    const back = (doc.body as unknown as { scrollTop?: number } | null)?.scrollTop ?? 0;
    el.remove();
    writeScrollTop(doc.documentElement, back);
  };
}

/**
 * Lift the SDK root into the top layer so containment cannot trap it.
 * `popover="manual"` (never light-dismissed) + `showPopover()`. The UA hides a
 * `[popover]` element until it is shown, so the attribute and the call must
 * always ship together — that is why this returns null (and drops the attribute)
 * the moment `showPopover` throws.
 */
function installTopLayer(root: HTMLElement): (() => void) | null {
  try {
    root.setAttribute(TOP_LAYER_ATTR, '');
    root.setAttribute('popover', 'manual');
    (root as unknown as { showPopover: () => void }).showPopover();
  } catch {
    root.removeAttribute('popover');
    root.removeAttribute(TOP_LAYER_ATTR);
    return null;
  }
  return () => {
    try {
      (root as unknown as { hidePopover: () => void }).hidePopover();
    } catch {
      /* already closed / detached */
    }
    root.removeAttribute('popover');
    root.removeAttribute(TOP_LAYER_ATTR);
  };
}

/**
 * Proxy the viewport-scroll APIs onto the body scroller.
 *
 * Reads (`scrollY`, `pageYOffset`, `document.scrollingElement`) and writes
 * (`scrollTo`, `scrollBy`, `scroll`) both go, because with `html{overflow:hidden}`
 * the viewport scroller is frozen at 0: every "scroll to top" button, sticky-
 * header threshold and scroll-progress bar on the host would silently read 0
 * forever.
 *
 * Position is only half of it — a progress bar needs the EXTENT as well, so
 * `documentElement.scrollHeight`/`scrollWidth` are proxied too (see below).
 */
function installScrollFacade(): () => void {
  const win = window as unknown as Record<string, unknown>;
  const doc = document;
  const body = doc.body;
  const patches: Array<PatchedProperty> = [];
  const push = (p: PatchedProperty | null) => {
    if (p) patches.push(p);
  };
  const scroller = () => doc.body as unknown as HTMLElement;

  const readY = () => (scroller()?.scrollTop as number) ?? 0;
  const readX = () => (scroller()?.scrollLeft as number) ?? 0;

  push(patchProperty(win, 'scrollY', { get: readY }));
  push(patchProperty(win, 'pageYOffset', { get: readY }));
  push(patchProperty(win, 'scrollX', { get: readX }));
  push(patchProperty(win, 'pageXOffset', { get: readX }));

  // `scrollTo(x, y)` and `scrollTo(options)` are both legal, and Element.scrollTo
  // accepts the same two shapes — so forwarding is a straight relay.
  const relay = (method: 'scrollTo' | 'scrollBy') =>
    function forwarded(this: unknown, ...args: unknown[]) {
      const el = scroller() as unknown as Record<string, (...a: unknown[]) => void>;
      if (el && typeof el[method] === 'function') el[method](...args);
    };
  push(patchProperty(win, 'scrollTo', { value: relay('scrollTo'), writable: true }));
  push(patchProperty(win, 'scrollBy', { value: relay('scrollBy'), writable: true }));
  // `window.scroll` is an alias of scrollTo; libraries still use it.
  push(patchProperty(win, 'scroll', { value: relay('scrollTo'), writable: true }));

  // `document.scrollingElement` is the standards-mode way to ask "who scrolls?"
  // — it answers `documentElement`, which is now wrong.
  push(patchProperty(doc, 'scrollingElement', { get: () => doc.body }));

  // …and the pre-`scrollingElement` idiom, `documentElement.scrollTop`. These are
  // own accessors on the ELEMENT INSTANCE, so the restore is a `delete` that
  // un-shadows Element.prototype — `Element.prototype` itself is never touched.
  const html = doc.documentElement as unknown as object;
  if (html) {
    push(
      patchProperty(html, 'scrollTop', {
        get: readY,
        set: (v: number) => {
          const el = scroller();
          if (el) el.scrollTop = v;
        },
      }),
    );
    push(
      patchProperty(html, 'scrollLeft', {
        get: readX,
        set: (v: number) => {
          const el = scroller();
          if (el) el.scrollLeft = v;
        },
      }),
    );

    // ── scroll EXTENT ──────────────────────────────────────────────────────
    // Faking the position without the extent is worse than faking neither: the
    // canonical progress bar is `scrollY / (documentElement.scrollHeight -
    // documentElement.clientHeight)`, and `html{height:100%;overflow:hidden}`
    // collapses that denominator to ZERO while our proxied scrollY keeps
    // climbing — Infinity%, not 0%. MEASURED (Chrome 151, 3091px page):
    // documentElement.scrollHeight 3091 → 813 (== clientHeight) after the
    // harness, back to 3091 on teardown, with body.scrollHeight holding the
    // honest 3091 throughout. The body scroller is where the extent lives now,
    // exactly as it is where the position lives.
    const readExtentY = () => (scroller()?.scrollHeight as number) ?? 0;
    const readExtentX = () => (scroller()?.scrollWidth as number) ?? 0;
    push(patchProperty(html, 'scrollHeight', { get: readExtentY }));
    push(patchProperty(html, 'scrollWidth', { get: readExtentX }));
    // `clientHeight` is deliberately NOT touched: it reports the VIEWPORT, which
    // the scroller handover does not change, and it is the honest half of the
    // progress-bar arithmetic. (`clientWidth` IS shifted — by the viewport
    // facade, for the dock offset, which is a different question.)
  }

  // ── scroll EVENTS ────────────────────────────────────────────────────────
  // Verified against the DOM spec, not assumed: a `scroll` event fired at an
  // ELEMENT does not bubble; only the viewport-scroll case targets `Document`
  // and reaches `window`. So with the body scrolling, every
  // `window.addEventListener('scroll')` on the host goes SILENT — the single
  // most common analytics/lazy-load/sticky hook on a marketing site.
  //
  // We re-fire on `document` with `bubbles: true`, which covers document AND
  // window listeners in one dispatch (window is in a document event's path).
  // The capture listener is a self-heal: if this engine ever DOES deliver a
  // native document-targeted scroll, we go quiet rather than double-fire
  // someone's pageview counter. It must ignore the body's own event, which a
  // capture listener sees regardless of `bubbles`. The suppression is a WINDOW,
  // not a latch: a single stray native scroll (a programmatic scroll of the
  // clipped root, say) must not silence forwarding for the rest of the visit.
  const FORWARDED = '__floeForwardedScroll';
  const NATIVE_SUPPRESS_MS = 500;
  let lastNativeScrollAt = 0;
  const onCapture = (e: Event) => {
    if ((e as unknown as Record<string, unknown>)[FORWARDED]) return;
    if (e.target === doc) lastNativeScrollAt = Date.now();
  };
  const onBodyScroll = () => {
    if (lastNativeScrollAt && Date.now() - lastNativeScrollAt < NATIVE_SUPPRESS_MS) return;
    try {
      const ev = new Event('scroll', { bubbles: true, cancelable: false });
      (ev as unknown as Record<string, unknown>)[FORWARDED] = true;
      doc.dispatchEvent(ev);
    } catch {
      /* no Event constructor (non-DOM env) — nothing to forward */
    }
  };
  let removeListeners = () => {};
  if (body && typeof body.addEventListener === 'function') {
    window.addEventListener('scroll', onCapture, true);
    body.addEventListener('scroll', onBodyScroll, { passive: true });
    removeListeners = () => {
      window.removeEventListener('scroll', onCapture, true);
      body.removeEventListener('scroll', onBodyScroll);
    };
  }

  return () => {
    removeListeners();
    // Reverse order: last patched, first restored.
    for (let i = patches.length - 1; i >= 0; i--) restoreProperty(patches[i]);
  };
}

/**
 * Patch `innerWidth`, `documentElement.clientWidth` and `matchMedia` to report
 * viewport-minus-dock while docked, and fire a synthetic `resize` whenever that
 * number changes.
 *
 * `clientWidth` is in that list because `innerWidth` is only one of the two
 * idioms and not the one jQuery uses: `$(window).width()` resolves to
 * `elem.document.documentElement.clientWidth`, and per CSSOM-View the ROOT
 * element's clientWidth is defined as the viewport — so it keeps answering the
 * full width no matter how squeezed `<html>`'s box is. MEASURED docked (Chrome
 * 151, 1440 real / 400 dock): innerWidth 1040, documentElement.clientWidth
 * **1440**, documentElement.getBoundingClientRect().width 1040. Two halves of
 * the same page disagreeing is worse than both being wrong.
 *
 * The dock state is read from the v1 attribute rather than pushed in by the
 * view: one source of truth, and it is automatically right for the cases the
 * view does NOT squeeze at all (overlay/fullscreen tiers, demo full-screen) —
 * the attribute is absent there, so the facade reports the real viewport.
 */
function installViewportFacade(getDockWidth: () => number): () => void {
  const win = window as unknown as Record<string, unknown>;
  const patches: Array<PatchedProperty> = [];
  const push = (p: PatchedProperty | null) => {
    if (p) patches.push(p);
  };

  const innerWidthDesc = findDescriptor(win, 'innerWidth');
  const realWidth = () => {
    const v = readThrough(innerWidthDesc, win);
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
  };
  const nativeMatchMedia =
    typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : undefined;

  // Publish the pristine references BEFORE patching, so the SDK's own hooks
  // never read the lie (see ./native-viewport for the feedback loop this
  // prevents).
  setNativeViewport({ matchMedia: nativeMatchMedia, innerWidth: realWidth });

  /** Current dock width, or 0 whenever the page is not actually squeezed. */
  const offset = () => (isDocked() ? normalizeSqueezeWidth(getDockWidth()) : 0);

  push(
    patchProperty(win, 'innerWidth', {
      get: () => Math.max(0, realWidth() - offset()),
    }),
  );

  // …and the other viewport-width idiom (see the docblock). An own accessor on
  // the ELEMENT INSTANCE, like documentElement.scrollTop in the scroll facade —
  // `Element.prototype` is never touched, and the restore is the `delete` that
  // un-shadows it. Skipped entirely when the host object has no numeric
  // clientWidth (the node/SSR path), so we never invent a 0 where the platform
  // had nothing.
  const htmlEl = (document.documentElement ?? null) as unknown as object | null;
  if (htmlEl) {
    const clientWidthDesc = findDescriptor(htmlEl, 'clientWidth');
    const realClientWidth = () => {
      const v = readThrough(clientWidthDesc, htmlEl);
      return typeof v === 'number' && Number.isFinite(v) ? v : null;
    };
    if (realClientWidth() !== null) {
      push(
        patchProperty(htmlEl, 'clientWidth', {
          get: () => Math.max(0, (realClientWidth() ?? 0) - offset()),
        }),
      );
    }
  }

  if (nativeMatchMedia) {
    // Rewrites at CALL time, so it only reaches queries created AFTER this
    // patch. A MediaQueryList the host registered at page load — the normal
    // idiom, and we boot after page load — is unreachable: its query cannot be
    // rewritten from outside and it fires no `change` on dock (measured: 0
    // events across a full dock+undock). The synthetic `resize` below is the
    // only notification such a host gets. See the module docblock.
    push(
      patchProperty(win, 'matchMedia', {
        value: function patchedMatchMedia(query: string) {
          const px = offset();
          return nativeMatchMedia(px > 0 ? rewriteMediaQuery(String(query), px) : query);
        },
        writable: true,
      }),
    );
  }

  // Dock/undock (and, in the same stroke, any width change: the width only ever
  // changes by re-docking) is the moment every host `resize` handler has to
  // re-read the now-different innerWidth.
  let stopObserver = () => {};
  if (typeof MutationObserver === 'function') {
    let lastOffset = offset();
    const observer = new MutationObserver(() => {
      const next = offset();
      if (next === lastOffset) return;
      lastOffset = next;
      try {
        window.dispatchEvent(new Event('resize'));
      } catch {
        /* no Event constructor — skip the notification, keep the patches */
      }
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [DOCKED_ATTR],
    });
    stopObserver = () => observer.disconnect();
  }

  return () => {
    stopObserver();
    for (let i = patches.length - 1; i >= 0; i--) restoreProperty(patches[i]);
    clearNativeViewport();
  };
}

/**
 * Apply the harness. Returns an idempotent teardown that restores every patched
 * global and removes every injected style — call it from the SDK's `destroy`.
 *
 * No-op (returning a no-op) when: both flags are off, there is no DOM, the plan
 * negotiated down to nothing, or a harness is already active. Flags off must be
 * indistinguishable from this module not existing.
 */
export function applySqueezeCompat(options: ApplyCompatOptions): () => void {
  const noop = () => {};
  const requested = options.compat;
  if (!requested || !isCompatRequested(requested)) return noop;
  if (!hasDom() || typeof window === 'undefined') return noop;
  // Single harness per page: a second SDK instance must not double-patch (the
  // second teardown would restore OUR accessor as if it were native).
  if (active) return noop;

  const caps = options.capabilities ?? detectCompatCapabilities(options.root);
  const plan = resolveCompatPlan(requested, caps);
  if (!isCompatRequested(plan)) return noop;

  const teardowns: Array<() => void> = [];
  if (plan.containment) {
    // CSS strictly BEFORE showPopover(): the UA dresses an open popover as a
    // dialog (opaque `background-color: canvas`), and our root is a full-viewport
    // box — showing it one frame ahead of the neutralising rule would flash a
    // white sheet over the customer's page. Teardown unwinds in reverse, so
    // hidePopover() likewise happens while that rule is still installed.
    const dropCss = installHarnessCss();
    const topLayer = options.root ? installTopLayer(options.root) : null;
    if (!topLayer) {
      // The API said yes and the call still failed (detached root, an engine
      // that hides popovers). Refuse the whole bundle rather than ship a rail
      // trapped inside the squeezed box — including rolling the sheet back out.
      dropCss();
      plan.containment = false;
    } else {
      teardowns.push(dropCss);
      teardowns.push(topLayer);
      teardowns.push(installScrollFacade());
    }
  }
  if (plan.viewportFacade) {
    const width =
      typeof options.dockWidth === 'function' ? options.dockWidth : () => Number(options.dockWidth);
    teardowns.push(installViewportFacade(width));
  }

  if (!teardowns.length) return noop;

  active = { plan, teardowns };
  let released = false;
  return () => {
    if (released) return;
    released = true;
    releaseSqueezeCompat();
  };
}

/**
 * Tear the harness down now, whoever asked. Safe to call when nothing is
 * applied — the SDK's `disconnect()` calls it unconditionally, the same belt
 * `forceReleaseSqueeze()` is for v1.
 */
export function releaseSqueezeCompat(): void {
  const current = active;
  active = null;
  if (!current) return;
  // Strictly reverse install order: the globals are handed back first (so the
  // scroll carry-back in the sheet's teardown reads a native body scroller), the
  // root leaves the top layer while the neutralising rule still applies, and the
  // sheet goes last.
  for (let i = current.teardowns.length - 1; i >= 0; i--) {
    try {
      current.teardowns[i]();
    } catch {
      /* keep unwinding — one failed restore must not strand the rest */
    }
  }
}

/** What is actually installed right now (null = nothing). Test/debug seam. */
export function activeCompatPlan(): SidebarCompat | null {
  return active ? { ...active.plan } : null;
}
